<template>
  <div :class="`form-group c-editor ${error ? 'has-error' : ''}`" id="text-editor-wrapper">
    <label v-show="label" class="control-label">
      {{ label }}
      <span v-if="requiredMark" class="text-danger">*</span>
    </label>
    <textarea
      :value="value"
      :id="id || _uid"
      v-bind="$attrs"
      @blur="$emit('blur', $event.target.value)"
      @change="$emit('change', $event.target.value)"
      class="form-control"
    ></textarea>
    <div v-if="snippets.length" style="margin-bottom: 5px;">
      <i>Available Code Snippets:</i>
      <br />
      <!-- snippets-->
      <span v-for="item in snippets" :key="item.value">
        <input
          type="button"
          :value="item.value"
          @click="insertSnippet(item.custom_value, item.value)"
          data-toggle="tooltip"
          data-placement="top"
          rel="tooltip"
          :title="item.title"
          :disabled="!!disabled"
        />
      <i v-if="item.hint">{{item.hint}}</i>&nbsp;
      </span>
    </div>
    <small class="text-danger" v-if="error">{{ error }}</small>
    <span class="help-block" v-if="help">{{ help }}</span>
  </div>
</template>

<script>
export default {
  props: {
    height:{
      type: Number,
      default: 200,
    },
    mode: {
      type: String,
      default: 'text',
    },
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    error: {
      type: String,
      default: ''
    },
    help: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    snippets: {
      type: Array,
      default: () => []
    },
    requiredMark: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: String,
      default: ''
    },
    toolbar: {
      type: Array,
      default: () => [
        ['style', ['style']],
        ['font', ['bold', 'italic', 'underline', 'clear']],
        ['fontname', ['fontname']],
        ['fontNamesIgnoreCheck', ['Open Sans']],
        ['fontsize', ['fontsize']],
        ['color', ['color']],
        ['para', ['ul', 'ol', 'paragraph']],
        ['height', ['height']],
        ['table', ['table']],
        ['insert', ['hr', 'link']],
        ['view', ['codeview']]
      ]
    }
  },
  data() {
    return {
      // Last content Summernote itself reported upwards. Lets the `value`
      // watcher ignore the echo of what the user just typed, which would
      // otherwise reset the caret to the end on every keystroke.
      lastEmitted: null,
    }
  },
  watch: {
    value(val) {
      const next = val || ''

      if (next === (this.lastEmitted || '') || this.isEditorFocused()) {
        return
      }

      this.setEditorContent(next)
    },
  },
  mounted() {
    const self = this

    this.lastEmitted = this.value

    this.editorEl().summernote({
      tabsize: 2,
      height: this.height,
      toolbar: this.toolbar,
      callbacks: {
        onInit: function() {
          if (self.mode === 'code') {
            $("div.note-editor button.btn-codeview").click();
          }
        },

        onChange: function(contents) {
          self.lastEmitted = contents
          self.$emit('input', contents)
        },

        onPaste: function(e) {
          let bufferText = ((e.originalEvent || e).clipboardData || window.clipboardData).getData('Text')

          e.preventDefault()

          setTimeout( () => {
            document.execCommand( 'insertText', false, bufferText )
          }, 10 )
        },

        onImageUpload: function(file) {
          const uploadCareHost = 'https://ucarecdn.com'

          $(`#text-editor-wrapper`).spin('small')

          let fileUpload = uploadcare.fileFrom('object', file[0])

          fileUpload.done((fileInfo) => {
            if (fileInfo.isImage) {
              let image = $('<img>')
                .attr('src', `${uploadCareHost}/${fileInfo.uuid}/-/format/auto/-/preview/800x600/-/stretch/off/`)
                .attr('style', 'width:100%;')

              self.editorEl().summernote('insertNode', image[0]);
            } else {
              let file = $('<a download>Download File</a>').attr('href', fileInfo.originalUrl)

              self.editorEl().summernote('insertNode', file[0]);
            }

            $(`#text-editor-wrapper`).spin(false)
          })
        }
      }
    })

    // Codeview typing goes into Summernote's own textarea and triggers no
    // `change` until the user switches back to WYSIWYG, so edits made in code
    // mode would never reach the model. Mirror them as they are typed.
    this.codableEl().on('input.cEditor', function() {
      if (!self.isCodeviewActive()) {
        return
      }

      self.lastEmitted = $(this).val()
      self.$emit('input', self.lastEmitted)
    })

    // Summernote reads the textarea only at init time. When the bound model is
    // still loading (async fetch resolving after the child mounted), push the
    // value explicitly so the editor never starts out of sync.
    if (this.value) {
      this.setEditorContent(this.value)
    }
  },
  beforeDestroy() {
    this.codableEl().off('input.cEditor')

    if (this.isEditorReady()) {
      this.editorEl().summernote('destroy')
    }
  },
  methods: {
    editorEl() {
      return $(this.$el.querySelector('textarea'))
    },

    codableEl() {
      return $(this.$el.querySelector('.note-codable'))
    },

    isEditorReady() {
      return !!this.$el.querySelector('.note-editor')
    },

    isCodeviewActive() {
      const editor = this.$el.querySelector('.note-editor')

      return !!editor && editor.classList.contains('codeview')
    },

    // Covers both panes: the WYSIWYG .note-editable and the codeview
    // .note-codable, so the `value` watcher never overwrites either while the
    // caret is inside it.
    isEditorFocused() {
      const area = this.$el.querySelector('.note-editing-area')

      return !!area && area.contains(document.activeElement)
    },

    setEditorContent(html) {
      if (!this.isEditorReady()) {
        return
      }

      const $el = this.editorEl()

      if ($el.summernote('code') === html) {
        return
      }

      this.lastEmitted = html
      $el.summernote('code', html)
    },

    insertSnippet(customSnippet, snippet) {
      if (customSnippet) {
        this.editorEl().summernote('pasteHTML', customSnippet);
      } else {
        this.editorEl().summernote('editor.insertText', snippet);
      }
    }
  }
}
</script>

<style lang="scss">
.c-editor {
  .note-editor.note-frame .note-editing-area .note-editable {
    padding-top: 30px;
  }
  .note-editor.note-frame {
    margin-bottom: 5px !important;
  }
  img {
    max-width: 100%;
  }
}

.has-error {
  .note-editor.note-frame {
    border: 1px solid #a94442 !important;
  }

  .panel-default > .panel-heading {
    background-color: rgba(169, 68, 66, 0.2);
    border-color: #a94442;
  }
}
</style>
